import { useEffect, useMemo, useState, type ChangeEvent } from "react";
import {
  Box,
  Card,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  List,
  ListItem,
  MenuItem,
  Switch,
  Text,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { listingsModel } from "models/listings";
import {
  useLocation,
  useNavigate,
  useParams,
  type Location,
} from "react-router-dom";
import EmptyRecord from "assets/icons/medium/empty-record.svg?react";
import { Empty } from "components/empty";
import { EmptyText } from "components/empty-text";
import { FeaturePreferencesConditionalComponent } from "components/feature-preferences/feature-preferences-conditional-component";
import {
  MultiSelectInput,
  type MenuOption,
} from "components/inputs/multi-select-input";
import { SelectInput } from "components/inputs/select-input";
import { useMatchMenuWidth } from "components/inputs/select-utils";
import { ScreenWrapper } from "components/screen-wrapper";
import { TotalCountBadge } from "components/total-count-badge";
import { useIsAgencyFeaturePreferenceEnabled } from "hooks/use-is-agency-feature-preference-enabled";
import { useWhichWordFactory } from "modules/vendor-portal/hooks/use-which-words";
import { ShowOnlyBadge } from "modules/vendor-portal/screens/feedback/components/show-only-badge";
import {
  FeedbackGroupByOption,
  groupFeedback,
  hasNonZeroInterestLevel,
  resolveEffectiveGroupBy,
  resolveEffectiveShowOnly,
  shouldShowFeedback,
  type FeedbackItem,
  type FeedbackTypeMenuOption,
  type ShowOnlyOptions,
} from "modules/vendor-portal/screens/feedback/utils";
import { FeedbackListItem } from "modules/vendor-portal/screens/overview/components/feedback/feedback-list-item";
import { ListingPerformance } from "modules/vendor-portal/screens/overview/components/listing-performance/listing-performance";
import { PriceIndication } from "modules/vendor-portal/screens/overview/components/price-indication/price-indication";

const FEEDBACK_OPTIONS: FeedbackTypeMenuOption[] = [
  { value: "enquiry", label: "Enquiry" },
  { value: "inspection", label: "inspection" },
  { value: "ofi", label: "Open home" },
  { value: "followup", label: "Follow-up" },
  { value: "offer", label: "Offer feedback" },
] as const;

type FeedbackOptionsGroup = {
  label: { id: string; value: string };
  options: MenuOption[];
};

const GROUP_BY_OPTIONS: FeedbackOptionsGroup[] = [
  {
    label: { id: "group-by", value: "Group by" },
    options: [
      { value: FeedbackGroupByOption.Date, label: "Date" },
      { value: FeedbackGroupByOption.Name, label: "Name" },
      { value: FeedbackGroupByOption.InterestLevel, label: "Interest level" },
      { value: FeedbackGroupByOption.FeedbackType, label: "Feedback type" },
    ],
  },
];

const DEFAULT_GROUP_BY_OPTION = GROUP_BY_OPTIONS[0].options[0].value;
const DEFAULT_FILTERS = FEEDBACK_OPTIONS.map((x) => x.value);

export type FeedbackLocationState = {
  groupBy?: FeedbackGroupByOption;
  filters?: FeedbackTypeMenuOption["value"][];
  showOnly?: ShowOnlyOptions;
};

export function FeedbackScreen() {
  const { propertyId } = useParams();
  const { data, isLoading } = useQuery(listingsModel.queries.read(propertyId));
  const { state } = useLocation() as Location<FeedbackLocationState | null>;
  const [filters, setFilter] = useState<string[]>(
    state?.filters ?? DEFAULT_FILTERS,
  );
  const [groupByValue, setGroupByValue] = useState<string>(
    state?.groupBy ?? DEFAULT_GROUP_BY_OPTION,
  );
  const [showEmptyEnquiries, setShowEmptyEnquiries] = useState<boolean>(false);

  const matchMenuWidth = useMatchMenuWidth();

  const navigate = useNavigate();

  const ww = useWhichWordFactory();

  const isAgencyFeaturePreferenceEnabled =
    useIsAgencyFeaturePreferenceEnabled();
  const showInterestLevel = isAgencyFeaturePreferenceEnabled(
    "vendor_portal.functionality.interest_level.show_interest_level_data",
  );

  useEffect(() => {
    if (state?.groupBy) {
      setGroupByValue(state.groupBy);
    }
  }, [state?.groupBy]);

  const groupByOptions: FeedbackOptionsGroup[] = useMemo(
    () =>
      showInterestLevel
        ? GROUP_BY_OPTIONS
        : GROUP_BY_OPTIONS.map((group) => ({
            ...group,
            options: group.options.filter(
              (option) => option.value !== FeedbackGroupByOption.InterestLevel,
            ),
          })),
    [showInterestLevel],
  );

  const effectiveGroupByValue = useMemo(
    () => resolveEffectiveGroupBy(groupByValue, showInterestLevel),
    [groupByValue, showInterestLevel],
  );

  const effectiveShowOnly = useMemo(
    () => resolveEffectiveShowOnly(state?.showOnly, showInterestLevel),
    [state?.showOnly, showInterestLevel],
  );

  if (!data) {
    return null;
  }

  const allFeedback = showEmptyEnquiries
    ? ([
        ...(data.applicant_feedback?.entries || []),
        ...(data.agent_updates?.entries || []),
      ] as FeedbackItem[])
    : ([
        ...(data.applicant_feedback?.entries || []),
        ...(data.agent_updates?.entries || []),
      ].filter((i) =>
        i.type.id === "enquiry" ? !!i.note : true,
      ) as FeedbackItem[]);

  const filteredFeedback = allFeedback.filter((i) => {
    const isCorrectType = filters.includes(i.type.id);

    if (!isCorrectType) {
      return false;
    }
    if (effectiveShowOnly) {
      return shouldShowFeedback(i, effectiveShowOnly);
    }

    return true;
  });
  const feedbackToShow = groupFeedback(
    filteredFeedback,
    effectiveGroupByValue as FeedbackGroupByOption,
  );

  const localisedFeedbackOptions = FEEDBACK_OPTIONS.map((option) => {
    return {
      ...option,
      label: ww(option.label),
    };
  });

  const hasInterestLevelChart =
    showInterestLevel &&
    hasNonZeroInterestLevel(
      data?.summary?.count_of_interested_groups_by_interest_level?.value,
    );

  const hasPriceIndication =
    (data.summary?.list_of_price_indications?.value ?? []).length > 0;

  const hasCharts = hasPriceIndication || hasInterestLevelChart;
  const handleToggleHideEmpty = (e: ChangeEvent<HTMLInputElement>) => {
    setShowEmptyEnquiries(e.target.checked);
  };
  return (
    <ScreenWrapper>
      {hasCharts && (
        <Heading size={{ base: "2xl", md: "lg" }} fontWeight={700} as={"h1"}>
          Feedback
        </Heading>
      )}

      <Flex direction={"row"} wrap={"wrap"} gap={{ base: 14, md: 6 }}>
        {hasInterestLevelChart && (
          <Box flex={1} minW={{ base: "100%", sm: "300px" }} minH={"342px"}>
            <ListingPerformance summary={data.summary} isLoading={isLoading} />
          </Box>
        )}
        <FeaturePreferencesConditionalComponent
          feature={
            "vendor_portal.functionality.graphs.show_price_indicator_chart"
          }
        >
          {hasPriceIndication && (
            <Box
              flex={1}
              minW={{ base: "100%", sm: "300px" }}
              minH={{ base: 0, md: "342px" }}
            >
              <PriceIndication
                priceAdvertiseAs={data.price_advertise_as}
                priceIndications={
                  data.summary?.list_of_price_indications?.value || []
                }
                isLoading={isLoading}
              />
            </Box>
          )}
        </FeaturePreferencesConditionalComponent>
      </Flex>

      <Flex
        direction={{ base: "column", sm: "row" }}
        align={{ base: "flex-start", sm: "center" }}
        w={"100%"}
        gap={{ base: 4, sm: 0 }}
        justify={"space-between"}
      >
        <HStack spacing={3} w={"100%"}>
          <Heading
            {...(hasCharts
              ? {
                  fontSize: "xl",
                  fontWeight: 600,
                  lineHeight: "28px",
                  whiteSpace: "nowrap",
                }
              : { as: "h1", base: "2xl", md: "lg", fontWeight: 700 })}
          >
            Feedback Notes
          </Heading>
          ;
          <TotalCountBadge count={filteredFeedback?.length} />
          <ShowOnlyBadge
            showOnly={effectiveShowOnly}
            clear={() =>
              navigate("", {
                state: { ...state, showOnly: undefined },
                preventScrollReset: true,
              })
            }
          />
        </HStack>
        <Flex
          direction={{ base: "column", sm: "row" }}
          gap={{ base: 4, sm: 3 }}
          w={"100%"}
          justify={{ base: "space-around", sm: "flex-end" }}
        >
          <MultiSelectInput
            placeholder={"Feedback type"}
            options={localisedFeedbackOptions}
            value={filters}
            menuLabel={"Filter by"}
            onChange={setFilter}
            menuProps={{ matchWidth: matchMenuWidth }}
            additionalItems={
              <>
                <Divider my={2} />
                <MenuItem closeOnSelect={false}>
                  <FormControl
                    display={"flex"}
                    alignItems={"center"}
                    w={"full"}
                  >
                    <FormLabel
                      htmlFor={"show-empty-enquiries"}
                      mb={"0"}
                      flex={"1"}
                    >
                      Show enquiries without comments
                    </FormLabel>
                    <Switch
                      id={"show-empty-enquiries"}
                      colorScheme={"indigo"}
                      size={"lg"}
                      isChecked={showEmptyEnquiries}
                      onChange={handleToggleHideEmpty}
                    />
                  </FormControl>
                </MenuItem>
              </>
            }
          />
          <SelectInput
            placeholder={"Select group"}
            optionGroups={groupByOptions}
            value={effectiveGroupByValue}
            selectionPrefix={"Group by: "}
            onChange={(value) => setGroupByValue(value)}
            menuProps={{ matchWidth: matchMenuWidth }}
          />
        </Flex>
      </Flex>

      <List spacing={4}>
        {allFeedback.length === 0 && (
          <Empty
            icon={<Icon as={EmptyRecord} boxSize={"auto"} />}
            emptyMessage={"Hang tight! Feedback is on the way"}
            emptyDesc={
              "This section will be updated once feedback has been collected"
            }
          />
        )}
        {filteredFeedback.length === 0 && allFeedback.length > 0 ? (
          <EmptyText textAlign={"center"}>No feedback found</EmptyText>
        ) : (
          feedbackToShow.map(({ label, feedback }) => {
            if (feedback.length === 0) return null;
            return (
              <ListItem key={label}>
                <Flex direction={"column"} gap={3}>
                  <Text
                    fontSize={"xl"}
                    fontWeight={400}
                    color={"gray.600"}
                    mt={3}
                  >
                    {label}
                  </Text>
                  <Divider mb={3} />
                  {feedback
                    .sort((a, b) => {
                      if ("is_new" in a && "is_new" in b) {
                        return Number(b.is_new) - Number(a.is_new);
                      }
                      return 0;
                    })
                    .map((feedbackItem) => (
                      <Card
                        key={feedbackItem.id}
                        pt={3}
                        pr={10}
                        pb={4}
                        pl={3}
                        style={{ breakInside: "avoid-page" }}
                      >
                        <FeedbackListItem feedback={feedbackItem} />
                      </Card>
                    ))}
                </Flex>
              </ListItem>
            );
          })
        )}
      </List>
    </ScreenWrapper>
  );
}
